import { jwtDecode } from "jwt-decode";
import { BASE_URL, PUBLIC_ROUTES, STORAGE_KEYS } from "../constant/constant";
import { toastError, toastSuccess } from "../utils/toastUtils";
import axiosInstance from "./axiosService";
import storageService from "./storageService";

let tokenRefreshInterval = null;

const TOKEN_REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const getLocalToken = () => {
  try {
    let token = storageService.get(STORAGE_KEYS.ZINO_TOKEN);
    if (token && typeof token === "string") {
      return token;
    }
    const cookies = document.cookie.split(";");
    const tokenCookie = cookies.find((cookie) => cookie.startsWith("auth_token="));
    token = tokenCookie ? tokenCookie.split("=")[1] : null;
    if (token) {
      storageService.set(STORAGE_KEYS.ZINO_TOKEN, token);
      return token;
    }
    return "";
  } catch (error) {
    return "";
  }
};

const extractToken = (tokenWithBearer) => {
  if (typeof tokenWithBearer !== "string") {
    return "";
  }
  const parts = tokenWithBearer.split(" ");
  if (parts.length === 2 && parts[0] === "Bearer") {
    return parts[1];
  }
  return "";
};

const parseToken = (token) => {
  try {
    const withoutBearer = extractToken(token);
    return jwtDecode(withoutBearer);
  } catch {
    return null;
  }
};

const setToken = (token) => {
  storageService.set(STORAGE_KEYS.ZINO_TOKEN, token);
};

const removeToken = () => {
  storageService.remove(STORAGE_KEYS.ZINO_TOKEN);
  // remove token from cookies
  document.cookie = "auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/";
};
const logout = () => {
  removeToken();

  const pathname = window.location.pathname;
  const search = window.location.search;
  const fullPath = pathname + search;
  const encodedFullPath = encodeURIComponent(fullPath);

  const isOnPublicRoute = PUBLIC_ROUTES.includes(pathname);

  if (!isOnPublicRoute) {
    return `/?redirect=${encodedFullPath}`;
  }

  return fullPath;
};

const refreshToken = async () => {
  const token = getLocalToken();
  if (!token) return null;

  try {
    const { data } = await axiosInstance.post(`${BASE_URL}/api/v1/user/refresh-token`, null, {
      headers: { Authorization: token }
    });
    toastSuccess("Token refreshed successfully!");
    setToken(data);
    return data;
  } catch (err) {
    console.error("Error refreshing token:", err);
    toastError("Session expired. Please log in again.");
    return null;
  }
};

const checkAndRefreshToken = async () => {
  const token = getLocalToken();
  if (!token) return null;

  const decoded = parseToken(token);
  if (!decoded?.refresh_time) return token;

  const refreshTime = new Date(decoded.refresh_time * 1000);
  if (new Date() >= refreshTime) {
    return await refreshToken();
  }

  return token;
};

const startRefreshTimer = (callback) => {
  stopRefreshTimer();
  tokenRefreshInterval = setInterval(callback, TOKEN_REFRESH_INTERVAL_MS);
};

const stopRefreshTimer = () => {
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
    tokenRefreshInterval = null;
  }
};

export default {
  getLocalToken,
  setToken,
  removeToken,
  logout,
  parseToken,
  refreshToken,
  checkAndRefreshToken,
  startRefreshTimer,
  stopRefreshTimer
};
